import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAdvancedSettings } from '../api/AdvancedSettingsProvider'
import { advancedGet, advancedPost } from '../api/client'
import { clearLocal, readLocal, writeLocal } from '../api/localStore'
import { useAdvancedResource } from '../api/useAdvancedResource'
import { useEditMode } from '../dashboard/useEditMode'
import { useAdvancedEnabled } from '../useAdvancedEnabled'
import { PRODUCT_UPDATES, ProductUpdate } from './updates'

type Seen = { seen: string[] | null }

export type ProductUpdatesView = { mode: 'unseen' | 'all'; updates: ProductUpdate[] }

type SetCareTeamChatOpen = (open: boolean) => void

// Whether the open patient view's clinician can use the care team chat; `pending` until it can tell.
export type CareTeamChatAccess = 'pending' | 'unavailable' | 'available'

type LentCareTeamChat = { access: 'pending' | 'unavailable' } | { access: 'available'; setOpen: SetCareTeamChatOpen }

const NO_CARE_TEAM_CHAT: LentCareTeamChat = { access: 'unavailable' }

// A first visit's baseline until the service has it, so a failed write can't let later updates count as seen.
const PENDING_BASELINE_KEY = 'advanced.productUpdates.pendingBaseline.v1'

type ProductUpdatesContextValue = {
  unseen: ProductUpdate[]
  view: ProductUpdatesView | null
  open: () => void
  close: () => void
  markSeen: (updateIds: string[]) => Promise<void>
  careTeamChat: LentCareTeamChat
  lendCareTeamChat: (careTeamChat: LentCareTeamChat) => void
  reportFirstRunTourRunning: (running: boolean) => void
}

const ProductUpdatesContext = createContext<ProductUpdatesContextValue>({
  unseen: [],
  view: null,
  open: () => undefined,
  close: () => undefined,
  markSeen: () => Promise.resolve(),
  careTeamChat: NO_CARE_TEAM_CHAT,
  lendCareTeamChat: () => undefined,
  reportFirstRunTourRunning: () => undefined,
})

type Props = { updates?: ProductUpdate[]; children: React.ReactNode }

export const ProductUpdatesProvider = ({ updates: allUpdates = PRODUCT_UPDATES, children }: Props) => {
  const enabled = useAdvancedEnabled()
  const resource = useAdvancedResource((signal) => advancedGet<Seen>('/product-updates', signal), [enabled], !enabled)
  const autoStart = useAdvancedSettings().settings?.productUpdatesAutoStart
  const [editMode] = useEditMode()
  const [seen, setSeenState] = useState<string[] | null>(null)
  // Read by `markSeen`, so it stays stable while marks land.
  const seenNow = useRef<string[] | null>(null)
  const setSeen = useCallback((next: string[]) => {
    seenNow.current = next
    setSeenState(next)
  }, [])
  const [view, setView] = useState<ProductUpdatesView | null>(null)
  const [autoStartDecided, setAutoStartDecided] = useState(false)
  const [careTeamChat, lendCareTeamChat] = useState<LentCareTeamChat>(NO_CARE_TEAM_CHAT)
  const [firstRunTourRunning, setFirstRunTourRunning] = useState(false)
  // A first-run tour that starts in the same commit as the auto-open decision hasn't re-rendered yet.
  const firstRunTourRunningNow = useRef(false)
  const reportFirstRunTourRunning = useCallback((running: boolean) => {
    firstRunTourRunningNow.current = running
    setFirstRunTourRunning(running)
  }, [])
  // An update that opens the care team chat is only for someone who can use it, on an open patient.
  const updates = useMemo(
    () => allUpdates.filter(({ target }) => !target?.opensCareTeamChat || careTeamChat.access === 'available'),
    [allUpdates, careTeamChat]
  )

  useEffect(() => {
    if (resource.status !== 'ready') return
    if (resource.data.seen) {
      setSeen(resource.data.seen)
      return
    }
    // A clinician's first visit: what already shipped is covered by the first-run tour.
    const baseline = readLocal<string[] | null>(PENDING_BASELINE_KEY, null) ?? allUpdates.map(({ id }) => id)
    writeLocal(PENDING_BASELINE_KEY, baseline)
    setSeen(baseline)
    advancedPost<Seen>('/product-updates/seen', { updateIds: baseline })
      .then(() => clearLocal(PENDING_BASELINE_KEY))
      .catch(() => undefined)
  }, [resource, allUpdates, setSeen])

  const unseen = useMemo(() => (seen ? updates.filter(({ id }) => !seen.includes(id)) : []), [seen, updates])

  useEffect(() => {
    const held = firstRunTourRunning || firstRunTourRunningNow.current || editMode || careTeamChat.access === 'pending'
    if (autoStartDecided || !seen || autoStart === undefined || held) return
    setAutoStartDecided(true)
    if (autoStart && unseen.length > 0) setView({ mode: 'unseen', updates: unseen })
  }, [autoStartDecided, seen, autoStart, firstRunTourRunning, editMode, careTeamChat, unseen])

  const markSeen = useCallback(
    async (updateIds: string[]) => {
      // Before the first read, a write would start a row without the baseline.
      if (!seenNow.current) return
      const known = seenNow.current
      const added = updateIds.filter((id) => !known.includes(id))
      if (added.length === 0) return
      const merge = (ids: string[]) => setSeen([...new Set([...(seenNow.current ?? []), ...ids])])
      merge(added)
      // Merged rather than replaced: responses to earlier marks can arrive after later ones.
      const response = await advancedPost<Seen>('/product-updates/seen', { updateIds: added })
      if (response.seen) merge(response.seen)
    },
    [setSeen]
  )

  const open = useCallback(
    () => setView(unseen.length > 0 ? { mode: 'unseen', updates: unseen } : { mode: 'all', updates }),
    [unseen, updates]
  )
  const close = useCallback(() => setView(null), [])

  const value = useMemo(
    () => ({ unseen, view, open, close, markSeen, careTeamChat, lendCareTeamChat, reportFirstRunTourRunning }),
    [unseen, view, open, close, markSeen, careTeamChat, reportFirstRunTourRunning]
  )

  return <ProductUpdatesContext.Provider value={value}>{children}</ProductUpdatesContext.Provider>
}

export const useProductUpdates = (): Pick<ProductUpdatesContextValue, 'unseen' | 'view' | 'open'> => {
  const { unseen, view, open } = useContext(ProductUpdatesContext)

  return { unseen, view, open }
}

// For `ProductUpdatesTour` only.
export const useProductUpdatesWalkthrough = () => {
  const { view, close, markSeen, careTeamChat } = useContext(ProductUpdatesContext)

  return {
    view,
    close,
    markSeen,
    setCareTeamChatOpen: careTeamChat.access === 'available' ? careTeamChat.setOpen : undefined,
  }
}

// Product updates run from the shell, so the open patient view lends it the care team chat toggle.
export const useLendCareTeamChatToProductUpdates = (access: CareTeamChatAccess, setOpen: SetCareTeamChatOpen) => {
  const { lendCareTeamChat } = useContext(ProductUpdatesContext)

  useEffect(() => {
    lendCareTeamChat(access === 'available' ? { access, setOpen } : { access })

    return () => lendCareTeamChat(NO_CARE_TEAM_CHAT)
  }, [access, setOpen, lendCareTeamChat])
}

// Two walkthroughs block each other's clicks, so product updates hold their auto-open while the first-run tour runs.
export const useReportFirstRunTourRunning = () => useContext(ProductUpdatesContext).reportFirstRunTourRunning
